import React from 'react'
import { CountryCode, PhoneNumber } from 'libphonenumber-js'
import {
  getPhoneNumberFromString,
  ConvertedPhoneNumber,
  getError,
} from './utils'
import { setValue } from '../../utils/input'
import { useCallbackRef } from '../../utils/useCallbackRef'
import { usePrevious } from '../../utils/usePrevious'

export const DEFAULT_REGION: CountryCode = 'DE'

export interface PhoneReturnValues {
  error: string | undefined
  phoneNumber: PhoneNumber | null
  ref: React.RefCallback<HTMLInputElement>
  value?: string
  getProps: (props?: PhoneGetPropsAttributes) => PhoneGetProps
}

export interface PhoneProps {
  defaultRegion?: CountryCode
  value?: string
}

export interface PhoneGetPropsAttributes {
  error?: string
  id?: string
  name: string
}

export interface PhoneGetProps {
  id?: string
  type?: HTMLInputElement['type']
  inputRef: React.RefCallback<HTMLInputElement>
  error?: boolean
  helperText?: string
  name?: string
}

const usePhone = (inputProps: PhoneProps = {}): PhoneReturnValues => {
  const { defaultRegion = DEFAULT_REGION, value = '' } = inputProps
  const [phoneNumber, setPhoneNumber] = React.useState<ConvertedPhoneNumber>(
    () => getPhoneNumberFromString(value, defaultRegion),
  )

  const { number, formatted } = phoneNumber

  const { callbackRef, elementRef } = useCallbackRef<HTMLInputElement>(
    (element) => {
      if (element !== null) {
        setValue(element, formatted as string)
      }
    },
  )

  const previousFormatted = usePrevious(formatted)

  React.useEffect(() => {
    if (!elementRef.current) {
      return
    }

    // We only want to format the value if the caret is at the end
    if (previousFormatted?.length === elementRef.current.selectionStart) {
      setValue(elementRef.current, formatted as string)
    }
  }, [elementRef, formatted, previousFormatted?.length])

  const error = React.useMemo(() => {
    if (!number) {
      return undefined
    }

    return getError(number, formatted)
  }, [number, formatted])

  const [displayError, setDisplayError] = React.useState(true)

  const handleFocus = React.useCallback(() => {
    if (!error) {
      setDisplayError(false)
    }
  }, [error])

  const handleBlur = React.useCallback(() => {
    setDisplayError(true)
  }, [])

  const handleChange = React.useCallback(() => {
    const inputValue = elementRef.current?.value
      .replace(/[^0-9.+]/g, '')
      .replace(/(\..*?)\..*/g, '$1')
    setPhoneNumber(getPhoneNumberFromString(inputValue || '', defaultRegion))

    setDisplayError(false)
  }, [defaultRegion, elementRef])

  const preventNonNumericInput = React.useCallback(
    (e: KeyboardEvent) => {
      const nonDigit = RegExp('^[0-9]+$')
      if (
        (e.key === '+' && (elementRef.current?.value || '').length === 0) ||
        nonDigit.test(e.key)
      ) {
        return
      }

      e.preventDefault()
    },
    [elementRef],
  )

  React.useEffect(() => {
    if (!elementRef.current) {
      return undefined
    }

    const inputRef = elementRef.current

    inputRef.addEventListener('input', handleChange)
    inputRef.addEventListener('focus', handleFocus)
    inputRef.addEventListener('blur', handleBlur)
    // inputRef.addEventListener('keypress', preventNonNumericInput)

    return () => {
      inputRef.removeEventListener('input', handleChange)
      inputRef.removeEventListener('focus', handleFocus)
      inputRef.removeEventListener('blur', handleBlur)
      // inputRef.removeEventListener('keypress', preventNonNumericInput)
    }
  }, [
    elementRef,
    handleBlur,
    handleChange,
    handleFocus,
    preventNonNumericInput,
  ])

  const outputError = displayError ? error : undefined

  const getProps = React.useCallback(
    (props = {}): PhoneGetProps => {
      const { error: propError, name, id = name } = props

      return {
        id,
        name,
        type: 'tel',
        helperText: propError || outputError,
        error: !!(propError || outputError),
        inputRef: callbackRef,
      }
    },
    [callbackRef, outputError],
  )

  return {
    error: outputError,
    phoneNumber: number || null,
    ref: callbackRef,
    value: formatted as string,

    getProps,
  }
}

export { usePhone }
